/**
 * Processing Pipeline - Orquestador de filtros ordenados
 * 
 * FMT-03: Aplica filtros en orden óptimo según el formato detectado.
 * Evita regresiones al procesar cada tipo de documento con su estrategia.
 * 
 * ORDEN DE PROCESAMIENTO:
 * 1. Detectar formato (FMT-01)
 * 2. Aplicar filtros según formato:
 *    - ODT: UTF-8 → Separadores → Placeholders → Markdown
 *    - Markdown: Markdown → UTF-8 → Separadores
 *    - HTML: HTML stripper → Entidades → UTF-8
 *    - Spreadsheet: Separadores → Whitespace
 *    - Plain: Whitespace solo
 * 3. Extraer coordenadas/direcciones
 * 4. Validar rangos
 * 
 * @module pipeline/processingPipeline
 */

import { detectFormat, type DocumentFormat, type FormatAnalysis } from '@/utils/formatDetector';
import { cleanMarkdown } from '@/utils/markdownCleaner';
import { expandAbbreviations } from '@/lib/abbreviationExpander';

// ============================================================================
// TIPOS
// ============================================================================

/** Nivel de procesamiento */
export type ProcessingLevel = 'minimal' | 'standard' | 'aggressive';

/** Configuración del pipeline */
export interface PipelineConfig {
  /** Formato del documento (detectado o especificado) */
  format?: DocumentFormat | FormatAnalysis;
  /** Nivel de procesamiento */
  level?: ProcessingLevel;
  /** Nombre del archivo (para detección) */
  fileName?: string;
  /** Municipio (para contexto) */
  municipio?: string;
  /** Forzar filtros específicos */
  forceFilters?: FilterType[];
  /** Saltar filtros específicos */
  skipFilters?: FilterType[];
}

/** Tipos de filtro disponibles */
export type FilterType = 
  | 'utf8Correction'
  | 'abbreviationExpander'
  | 'thousandsSeparator'
  | 'placeholderRemoval'
  | 'markdownCleaner'
  | 'htmlStripper'
  | 'entityDecoder'
  | 'trimWhitespace'
  | 'linkRemoval';

/** Resultado del pipeline */
export interface PipelineResult {
  /** Texto procesado */
  text: string;
  /** Formato detectado */
  format: DocumentFormat;
  /** Confianza en la detección */
  formatConfidence: number;
  /** Filtros aplicados en orden */
  appliedFilters: FilterType[];
  /** Número de modificaciones por filtro */
  modifications: Record<FilterType, number>;
  /** Tiempo de procesamiento (ms) */
  processingTime: number;
}

/** Resultado de procesamiento por lotes */
export interface BatchPipelineResult {
  /** Registros procesados */
  records: Array<{ original: string; processed: string; filters: FilterType[] }>;
  /** Estadísticas globales */
  stats: {
    total: number;
    modified: number;
    totalModifications: number;
    avgFiltersPerRecord: number;
    processingTime: number;
  };
  /** Formato común detectado */
  format: DocumentFormat;
}

// ============================================================================
// ORDEN DE FILTROS POR FORMATO
// ============================================================================

/**
 * Define el orden óptimo de filtros para cada formato.
 * El orden importa: aplicar en secuencia incorrecta puede causar regresiones.
 */
export const FILTER_ORDER_BY_FORMAT: Record<DocumentFormat, FilterType[]> = {
  // ODT: Primero corregir UTF-8, luego abreviaturas, separadores y placeholders
  odt: [
    'utf8Correction',
    'abbreviationExpander',
    'thousandsSeparator',
    'placeholderRemoval',
    'markdownCleaner',  // Por si hay markdown en ODT exportado
    'trimWhitespace',
  ],
  
  // Markdown: Primero limpiar asteriscos para no confundir con UTF-8
  markdown: [
    'markdownCleaner',
    'linkRemoval',
    'utf8Correction',
    'abbreviationExpander',
    'thousandsSeparator',
    'trimWhitespace',
  ],
  
  // HTML: Primero eliminar tags, luego entidades
  html: [
    'htmlStripper',
    'entityDecoder',
    'utf8Correction',
    'abbreviationExpander',
    'thousandsSeparator',
    'trimWhitespace',
  ],
  
  // Spreadsheet: Principalmente separadores de miles
  spreadsheet: [
    'thousandsSeparator',
    'trimWhitespace',
  ],
  
  // Plain: Solo limpieza básica
  plain: [
    'trimWhitespace',
  ],
};

/**
 * Filtros adicionales por nivel de agresividad
 */
const EXTRA_FILTERS_BY_LEVEL: Record<ProcessingLevel, FilterType[]> = {
  minimal: [],
  standard: [],
  aggressive: ['utf8Correction', 'placeholderRemoval'],
};

// ============================================================================
// IMPLEMENTACIÓN DE FILTROS
// ============================================================================

/**
 * Registro de funciones de filtro
 */
const FILTER_FUNCTIONS: Record<FilterType, (text: string) => { text: string; modifications: number }> = {
  
  abbreviationExpander: (text) => {
    const result = expandAbbreviations(text);
    return {
      text: result.expanded,
      modifications: result.expansionsApplied.length,
    };
  },
  
  utf8Correction: (text) => {
    let modified = 0;
    let result = text;
    
    // Patrones UTF-8 mojibake más comunes (tier HOT)
    const corrections: [RegExp, string][] = [
      [/Ã±/g, 'ñ'],
      [/Ã¡/g, 'á'],
      [/Ã©/g, 'é'],
      [/Ã­/g, 'í'],
      [/Ã³/g, 'ó'],
      [/Ãº/g, 'ú'],
      [/Ã¼/g, 'ü'],
      [/Ã'/g, 'Ñ'],
      [/Ã/g, 'Á'],
      [/Ã‰/g, 'É'],
      [/Ã/g, 'Í'],
      [/Ã"/g, 'Ó'],
      [/Ãš/g, 'Ú'],
      [/Â°/g, 'º'],
      [/Âª/g, 'ª'],
      [/â€"/g, '–'],
      [/â€"/g, '—'],
      [/â€œ/g, '"'],
      [/â€/g, '"'],
    ];
    
    for (const [pattern, replacement] of corrections) {
      const matches = result.match(pattern);
      if (matches) {
        modified += matches.length;
        result = result.replace(pattern, replacement);
      }
    }
    
    return { text: result, modifications: modified };
  },
  
  thousandsSeparator: (text) => {
    let modified = 0;
    let result = text;
    
    // Patrón: números con puntos como separadores de miles (ej: 524.538 → 524538)
    // Solo si son grupos de 3 dígitos
    const pattern = /\b(\d{1,3})\.(\d{3})(?:\.(\d{3}))?\b/g;
    
    result = result.replace(pattern, (match, g1, g2, g3) => {
      modified++;
      return g3 ? `${g1}${g2}${g3}` : `${g1}${g2}`;
    });
    
    // También espacios como separadores (ej: 524 538 → 524538)
    const spacePattern = /\b(\d{3})\s+(\d{3})\b/g;
    result = result.replace(spacePattern, (match, g1, g2) => {
      modified++;
      return `${g1}${g2}`;
    });
    
    return { text: result, modifications: modified };
  },
  
  placeholderRemoval: (text) => {
    let modified = 0;
    let result = text;
    
    const placeholders = [
      /\b(indicar|completar|rellenar|pendiente)\b/gi,
      /^-{2,}$/gm,
      /^\.{3,}$/gm,
      /^_{3,}$/gm,
      /\bN\/A\b/gi,
      /\bxxx+\b/gi,
      /\bsin\s+datos?\b/gi,
    ];
    
    for (const pattern of placeholders) {
      const matches = result.match(pattern);
      if (matches) {
        modified += matches.length;
        result = result.replace(pattern, '');
      }
    }
    
    return { text: result, modifications: modified };
  },
  
  markdownCleaner: (text) => {
    const result = cleanMarkdown(text);
    return { text: result.cleaned, modifications: result.modifications };
  },
  
  htmlStripper: (text) => {
    let modified = 0;
    let result = text;
    
    // Eliminar tags HTML
    const tagPattern = /<\/?[a-z][^>]*>/gi;
    const matches = result.match(tagPattern);
    if (matches) {
      modified += matches.length;
      result = result.replace(tagPattern, '');
    }
    
    return { text: result, modifications: modified };
  },
  
  entityDecoder: (text) => {
    let modified = 0;
    let result = text;
    
    const entities: [RegExp, string][] = [
      [/&nbsp;/g, ' '],
      [/&amp;/g, '&'],
      [/&lt;/g, '<'],
      [/&gt;/g, '>'],
      [/&quot;/g, '"'],
    ];
    
    for (const [pattern, replacement] of entities) {
      const matches = result.match(pattern);
      if (matches) {
        modified += matches.length;
        result = result.replace(pattern, replacement);
      }
    }
    
    // Numeric entities: &#123; → character
    const numericPattern = /&#(\d+);/g;
    const numericMatches = result.match(numericPattern);
    if (numericMatches) {
      modified += numericMatches.length;
      result = result.replace(numericPattern, (_, code) => 
        String.fromCharCode(parseInt(code, 10))
      );
    }
    
    return { text: result, modifications: modified };
  },
  
  trimWhitespace: (text) => {
    const original = text;
    let result = text;
    
    // Normalizar espacios múltiples
    result = result.replace(/\s+/g, ' ');
    // Trim
    result = result.trim();
    
    const modified = original !== result ? 1 : 0;
    return { text: result, modifications: modified };
  },
  
  linkRemoval: (text) => {
    let modified = 0;
    let result = text;
    
    // [texto](url) → texto
    const linkPattern = /\[([^\]]+)\]\([^)]+\)/g;
    const matches = result.match(linkPattern);
    if (matches) {
      modified += matches.length;
      result = result.replace(linkPattern, '$1');
    }
    
    return { text: result, modifications: modified };
  },
};

// ============================================================================
// FUNCIONES PRINCIPALES
// ============================================================================

/**
 * Procesa un texto aplicando filtros en orden óptimo según formato.
 */
export function processText(text: string, config: PipelineConfig = {}): PipelineResult {
  const startTime = performance.now();
  
  // 1. Detectar formato
  const formatAnalysis = typeof config.format === 'object' 
    ? config.format 
    : detectFormat(text, config.fileName);
  
  const format = typeof config.format === 'string' 
    ? config.format 
    : formatAnalysis.format;
  
  // 2. Obtener orden de filtros para este formato
  let filters = [...FILTER_ORDER_BY_FORMAT[format]];
  
  // 3. Añadir filtros extra según nivel
  const level = config.level || 'standard';
  const extraFilters = EXTRA_FILTERS_BY_LEVEL[level];
  for (const f of extraFilters) {
    if (!filters.includes(f)) {
      filters.push(f);
    }
  }
  
  // 4. Aplicar forzados/saltados
  if (config.forceFilters) {
    for (const f of config.forceFilters) {
      if (!filters.includes(f)) {
        filters.unshift(f); // Añadir al principio
      }
    }
  }
  if (config.skipFilters) {
    filters = filters.filter(f => !config.skipFilters!.includes(f));
  }
  
  // 5. Aplicar filtros en orden
  let processed = text;
  const appliedFilters: FilterType[] = [];
  const modifications: Record<FilterType, number> = {} as Record<FilterType, number>;
  
  for (const filterType of filters) {
    const filterFn = FILTER_FUNCTIONS[filterType];
    if (filterFn) {
      const result = filterFn(processed);
      if (result.modifications > 0) {
        processed = result.text;
        appliedFilters.push(filterType);
        modifications[filterType] = result.modifications;
      }
    }
  }
  
  const processingTime = performance.now() - startTime;
  
  return {
    text: processed,
    format,
    formatConfidence: formatAnalysis.confidence,
    appliedFilters,
    modifications,
    processingTime,
  };
}

/**
 * Procesa múltiples textos en lote.
 */
export function processBatch(
  texts: string[], 
  config: PipelineConfig = {}
): BatchPipelineResult {
  const startTime = performance.now();
  
  // Usar formato del config si está especificado, sino detectar
  let formatAnalysis: FormatAnalysis;
  if (config.format) {
    if (typeof config.format === 'object') {
      formatAnalysis = config.format;
    } else {
      // Formato string: crear FormatAnalysis manual
      formatAnalysis = {
        format: config.format,
        confidence: 100,
        indicators: [],
        recommendations: [],
      };
    }
  } else {
    // Detectar formato del primer texto representativo
    const sampleText = texts.slice(0, Math.min(5, texts.length)).join('\n');
    formatAnalysis = detectFormat(sampleText, config.fileName);
  }
  
  const records: Array<{ original: string; processed: string; filters: FilterType[] }> = [];
  let totalModifications = 0;
  let modifiedCount = 0;
  let totalFilters = 0;
  
  for (const text of texts) {
    const result = processText(text, { ...config, format: formatAnalysis });
    
    records.push({
      original: text,
      processed: result.text,
      filters: result.appliedFilters,
    });
    
    const mods = Object.values(result.modifications).reduce((a, b) => a + b, 0);
    totalModifications += mods;
    if (mods > 0) modifiedCount++;
    totalFilters += result.appliedFilters.length;
  }
  
  return {
    records,
    stats: {
      total: texts.length,
      modified: modifiedCount,
      totalModifications,
      avgFiltersPerRecord: texts.length > 0 ? totalFilters / texts.length : 0,
      processingTime: performance.now() - startTime,
    },
    format: formatAnalysis.format,
  };
}

/**
 * Obtiene los filtros que se aplicarían a un formato.
 */
export function getFiltersForFormat(
  format: DocumentFormat, 
  level: ProcessingLevel = 'standard'
): FilterType[] {
  const filters = [...FILTER_ORDER_BY_FORMAT[format]];
  const extra = EXTRA_FILTERS_BY_LEVEL[level];
  
  for (const f of extra) {
    if (!filters.includes(f)) {
      filters.push(f);
    }
  }
  
  return filters;
}

/**
 * Aplica un filtro específico a un texto.
 */
export function applyFilter(
  text: string, 
  filterType: FilterType
): { text: string; modifications: number } {
  const filterFn = FILTER_FUNCTIONS[filterType];
  if (!filterFn) {
    return { text, modifications: 0 };
  }
  return filterFn(text);
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  processText,
  processBatch,
  getFiltersForFormat,
  applyFilter,
  FILTER_ORDER_BY_FORMAT,
};
